import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "@/i18n";

export const Understanding: React.FC = () => {
  const t = useTranslation();
  const examples = t.understanding.examples;
  const [exampleIndex, setExampleIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setExampleIndex((current) => (current + 1) % examples.length);
    }, 4500);
    return () => clearInterval(interval);
  }, [examples.length]);

  const example = examples[exampleIndex];

  return (
    <>
      <div id="verstehen"></div>
      <div className="py-[72px] md:py-[120px]">
        <div className="max-w-screen-xl m-auto px-[38px] 2xl:px-0">
          <div className="flex flex-col md:flex-row md:items-center gap-[48px] md:gap-[80px]">
            <div className="w-full md:w-1/2">
              <p className="text-[#01C6FB] font-semibold tracking-[0.18em] uppercase text-[13px] md:text-[14px]">
                {t.understanding.overline}
              </p>
              <h2 className="mt-3 text-[#171A1F] font-bold text-[28px] leading-[38px] md:text-[40px] md:leading-[52px]">
                {t.understanding.headline}
              </h2>
              <p className="mt-5 text-[16px] leading-[26px] md:text-[18px] md:leading-[30px] text-base-600">
                {t.understanding.p1}
              </p>
              <p className="mt-4 text-[16px] leading-[26px] md:text-[18px] md:leading-[30px] text-base-600">
                {t.understanding.p2}
              </p>
            </div>

            {/* Rotating "said vs. possible goals" visual */}
            <div className="w-full md:w-1/2">
              <div className="relative min-h-[330px] md:min-h-[340px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={exampleIndex}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -16 }}
                    transition={{ duration: 0.45 }}
                    className="flex flex-col space-y-3"
                  >
                    <div className="rounded-[20px] border border-base-200 bg-[#FAFAFB] p-[24px] md:p-[28px]">
                      <p className="text-[12px] font-semibold tracking-[0.15em] uppercase text-base-500">
                        {t.understanding.saidLabel}
                      </p>
                      <p className="mt-2 text-[18px] md:text-[22px] font-bold text-[#171A1F]">
                        {example.said}
                      </p>
                    </div>
                    <div className="flex justify-center">
                      <span className="material-symbols-outlined text-[28px] text-[#01C6FB]">
                        arrow_downward
                      </span>
                    </div>
                    <div className="rounded-[20px] border-2 border-[#01C6FB] bg-[#F2FAFC] p-[24px] md:p-[28px]">
                      <p className="text-[12px] font-semibold tracking-[0.15em] uppercase text-[#0195bd]">
                        {t.understanding.meaningsLabel}
                      </p>
                      <ul className="mt-3 space-y-2">
                        {example.meanings.map((meaning) => (
                          <li
                            key={meaning}
                            className="flex items-center text-[16px] md:text-[19px] font-bold text-[#171A1F]"
                          >
                            <span className="mr-3 h-[6px] w-[6px] shrink-0 rounded-full bg-[#01C6FB]" />
                            {meaning}
                          </li>
                        ))}
                      </ul>
                      <p className="mt-4 text-[14px] md:text-[15px] text-base-600">
                        {t.understanding.clarify}
                      </p>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
              <div className="mt-3 flex justify-center">
                {examples.map((_, index) => (
                  <button
                    key={`example-dot-${index}`}
                    aria-label={`${t.understanding.exampleAria} ${index + 1}`}
                    onClick={() => setExampleIndex(index)}
                    className="flex items-center justify-center p-[12px]"
                  >
                    <span
                      className={
                        index === exampleIndex
                          ? "h-[8px] w-[24px] rounded-full bg-[#01C6FB] transition-all"
                          : "h-[8px] w-[8px] rounded-full bg-base-300 transition-all"
                      }
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-[56px] md:mt-[80px] grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {t.understanding.principles.map((principle, index) => (
              <motion.div
                key={principle.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: index * 0.12 }}
              >
                <p className="text-[40px] md:text-[48px] font-bold text-[#01C6FB] leading-none">
                  {`0${index + 1}`}
                </p>
                <h3 className="mt-3 text-[18px] md:text-[20px] font-bold text-[#171A1F]">
                  {principle.title}
                </h3>
                <p className="mt-2 text-[15px] leading-[25px] md:text-[16px] md:leading-[27px] text-base-600">
                  {principle.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};
